import React from 'react';
import { Button } from '@insurancemenu/imenu-components-lib';
import { IconCheckmark } from '@insurancemenu/imenu-components-lib/dist/icons';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { ButtonState, CompletionStatus, ProductTemplateChecklistOption } from 'types/productServiceV2';
import { PRODUCT_TEMPLATE_CARD_DATA } from 'global/checklists-helpers/ProductTemplateChecklistHelper';
import useRfqTemplate from 'global/hooks/useRfqTemplate';
import { createStyles } from './styles';
import {
  STAGE_CARD_CONTINUE_BTN_TEST_ID,
  STAGE_CARD_VIEW_EDIT_BTN_TEST_ID,
  STAGE_CARD_UNAVAILABLE_BTN_TEST_ID,
  STAGE_CARD_STATUS_TEST_ID,
  STAGE_CARD_TEST_ID,
} from '../../constants';

type CardStatusesTitlesObjectType = {
  [K in CompletionStatus]: string | JSX.Element;
};

type CardButtonStateObjectType = {
  [K in ButtonState]: string;
};

const BUTTON_STATE_TEXT: CardButtonStateObjectType = Object.freeze({
  VIEW_EDIT: 'View / Edit',
  UNAVAILABLE: 'Unavailable',
  CONTINUE: 'Continue',
});

export interface ProductTemplateChecklistCardProps {
  option: ProductTemplateChecklistOption;
  templateId: string;
}

const ProductTemplateStageCard = ({ option, templateId }: ProductTemplateChecklistCardProps): JSX.Element => {
  const classes = createStyles();
  const { card, completionStatus, buttonState } = option;

  const history = useHistory();

  const { pageType } = useRfqTemplate();

  const onRedirect = (): void => {
    const cardDataObject = PRODUCT_TEMPLATE_CARD_DATA[card];
    if (templateId) {
      history.push(cardDataObject.redirectTo(templateId, pageType));
    }
  };

  const ContinueButton = (
    <Button
      color="primary"
      size="large"
      onClick={onRedirect}
      className={classes.actionButton}
      data-test-id={STAGE_CARD_CONTINUE_BTN_TEST_ID}
    >
      {BUTTON_STATE_TEXT[buttonState]}
    </Button>
  );

  const ViewEditButton = (
    <Button
      type="opaque"
      color="primary"
      size="large"
      onClick={onRedirect}
      className={classes.actionButton}
      data-test-id={STAGE_CARD_VIEW_EDIT_BTN_TEST_ID}
    >
      {BUTTON_STATE_TEXT[buttonState]}
    </Button>
  );

  const UnavailableButton = (
    <Button size="large" disabled className={classes.actionButton} data-test-id={STAGE_CARD_UNAVAILABLE_BTN_TEST_ID}>
      {BUTTON_STATE_TEXT[buttonState]}
    </Button>
  );

  const renderActionButton = (): JSX.Element | null => {
    switch (buttonState) {
      case ButtonState.UNAVAILABLE:
        return UnavailableButton;
      case ButtonState.VIEW_EDIT:
        return ViewEditButton;
      case ButtonState.CONTINUE:
        return ContinueButton;
      default:
        return null;
    }
  };

  const STATUSES_TITLES_OBJECT: CardStatusesTitlesObjectType = Object.freeze({
    COMPLETED: (
      <span className={classes.completedCardWrapper} data-test-id={STAGE_CARD_STATUS_TEST_ID}>
        <IconCheckmark className={classes.completedIcon} width="1.8rem" />
        <span className={classes.cardStatusText}>Completed</span>
      </span>
    ),
    READY_TO_BEGIN: (
      <span
        className={clsx(classes.cardStatusText, classes.readyToBeginStatusText)}
        data-test-id={STAGE_CARD_STATUS_TEST_ID}
      >
        Ready to begin
      </span>
    ),
    CANNOT_COMPLETE_YET: (
      <span className={classes.cardStatusText} data-test-id={STAGE_CARD_STATUS_TEST_ID}>
        Cannot complete yet
      </span>
    ),
    IN_PROGRESS: (
      <span
        className={clsx(classes.cardStatusText, classes.inProgressStatusText)}
        data-test-id={STAGE_CARD_STATUS_TEST_ID}
      >
        In-Progress
      </span>
    ),
  });

  return (
    <div className={classes.card} data-test-id={STAGE_CARD_TEST_ID}>
      <div>
        <h3 className={classes.title}>{PRODUCT_TEMPLATE_CARD_DATA[card].title}</h3>
        <p className={classes.text}>{PRODUCT_TEMPLATE_CARD_DATA[card].text}</p>
      </div>
      <div className={classes.rightSection}>
        {renderActionButton()} {STATUSES_TITLES_OBJECT[completionStatus]}
      </div>
    </div>
  );
};

export default ProductTemplateStageCard;
